import { Box, Flex } from "flicket-ui";
import { AnimatePresence } from "framer-motion";
import { useContext, useState } from "react";

import { FilterModal } from "~features/seated-reservation/components/filters/reservation.FilterModal";
import { Filters } from "~features/seated-reservation/components/filters/reservation.Filters";
import { MobileFilters } from "~features/seated-reservation/components/filters/reservation.MobileFilters";
import { Options } from "~features/seated-reservation/components/options-modal";
import { MobileOptions } from "~features/seated-reservation/components/options-modal/options.MobileLayout";
import SidebarLayout from "~features/seated-reservation/layout/Sidebar.layout";
import { SeatingChartWrapper } from "./components/seating-chart/SeatingChartWrapper";

import { UserContext } from "~context";
import { useIsMobile, useIsMounted } from "~hooks";

import { useReservationStore } from "~features/seated-reservation/store";
import { useSeatedReservation } from "./hooks/useSeatedReservation";
import { Release } from "./types";

type SeatedProps = {
  type: "event" | "membership" | "release";
  typeId: string;
  release: Release;
  defaultShowOptions: boolean;
};

export const Seated = ({
  type,
  typeId,
  release,
  defaultShowOptions,
}: SeatedProps) => {
  const { isAdmin } = useContext(UserContext);

  const {
    changeTickets,
    event,
    handleOrderCreation,
    priceComp,
    seatAmountOptions,
  } = useSeatedReservation({
    type,
    typeId,
    release,
  });

  const isMobile = useIsMobile();
  const isMounted = useIsMounted();

  const [showOptions, setShowOptions] = useState(defaultShowOptions);
  const chartReady = useReservationStore((state) => state.chartReady);

  const handleClose = () => {
    setShowOptions(false);
  };

  return (
    <>
      <Filters
        amountDisabled={!!changeTickets?.length}
        seatAmountOptions={seatAmountOptions}
        filterOptions={event.venue.filterOptions}
      />
      <Flex
        justifyContent={["flex-end", "flex-start"]}
        overflow="hidden"
        position="relative"
        height={{
          _: isAdmin ? "calc(100vh - 94px)" : "calc(100vh - 64px)",
          md: isAdmin ? "calc(100vh - 155px)" : "calc(100vh - 122px)",
        }}
        maxHeight={{
          _: isAdmin ? "calc(100vh - 94px)" : "calc(100vh - 64px)",
          md: isAdmin ? "calc(100vh - 155px)" : "calc(100vh - 122px)",
        }}
      >
        <Flex w="100%" maxW="100%">
          {!isMobile && (
            <>
              <Box
                position="relative"
                d={{ _: "none", md: "block" }}
                width="100%"
                minW={{ _: "100%", md: "400px" }}
                maxW={{ _: "100%", md: "450px" }}
              >
                <SidebarLayout
                  event={event}
                  release={release}
                  changeTickets={changeTickets}
                  handleOrderCreation={handleOrderCreation}
                  priceComp={priceComp}
                  allowPublicComp={release?.allowPublicComp}
                />
              </Box>
              {/* Assuming isMounted us here to delay showing modal for smoother
              animation after the first render...? */}
              {chartReady && showOptions && !isMobile && (
                <AnimatePresence>
                  <FilterModal isOpen={showOptions} onClose={handleClose}>
                    <Options
                      handleClose={handleClose}
                      title={event.title}
                      startDate={event.startDate}
                      location={
                        event.venue?.name &&
                        `${event.venue?.name}, ${event.venue?.address?.city}`
                      }
                      seatAmountOptions={seatAmountOptions}
                    />
                  </FilterModal>
                </AnimatePresence>
              )}
            </>
          )}
          <Box position="relative" maxW="100%" width="100%" bg="N100">
            <SeatingChartWrapper
              event={event}
              release={release}
              changeTickets={changeTickets}
            />

            {isMobile && (
              <>
                <MobileFilters
                  amountDisabled={!!changeTickets?.length}
                  seatAmountOptions={seatAmountOptions}
                  filterOptions={event.venue.filterOptions}
                />
                <SidebarLayout
                  event={event}
                  release={release}
                  changeTickets={changeTickets}
                  handleOrderCreation={handleOrderCreation}
                  priceComp={priceComp}
                  allowPublicComp={release?.allowPublicComp}
                />
              </>
            )}
          </Box>
        </Flex>
        {chartReady && showOptions && isMounted && isMobile && (
          <MobileOptions showOptions={showOptions} onClose={handleClose}>
            <Options
              handleClose={handleClose}
              seatAmountOptions={seatAmountOptions}
            />
          </MobileOptions>
        )}
      </Flex>
    </>
  );
};
